/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IImageSettings, IViewPort } from '@msdyn365-commerce/core';
import { IFooterViewProps } from '@msdyn365-commerce-modules/footer';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IFooterItemConfig } from '../definition-extensions/footer-item.ext.props.autogenerated';

/**
 *
 * FooterItemView component.
 * @extends {React.PureComponent<IFooterViewProps>}
 */
export class FooterItemView extends React.PureComponent<IFooterViewProps> {
    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { FooterItem } = this.props;
        return <Module {...FooterItem}>{this._renderElement(this.props)}</Module>;
    }

    private _renderElement(props: IFooterViewProps): React.ReactNode | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const { heading, HeadingContainer, Link, linkText, image, text, EditableLink } = props;
        const config = props.config as IFooterItemConfig;

        let fixedImage = image;
        const imageSettings = (fixedImage as React.ReactElement | undefined)?.props.imageSettings as IImageSettings | undefined;

        if (fixedImage && imageSettings) {
            Object.keys(imageSettings.viewports).forEach((k) => {
                const key = k as keyof IViewPort;

                if (imageSettings.viewports[key]?.q) {
                    const parametersToFilter = [
                        'f'
                    ].filter(Boolean) as string[];
                    const parametersToAdd = [
                        config.forceWebp && 'f=webp',
                        !config.forceWebp && 'f=png'
                    ].filter(Boolean) as string[];
                    const parameters = [
                        ...(imageSettings.viewports[key]!.q as string)
                            .split('&')
                            .filter((param) => !parametersToFilter.includes(param.split('=')[0])),
                        ...parametersToAdd
                    ].filter(Boolean) as string[];
                    imageSettings!.viewports[key]!.q = parameters.join('&');
                }
            });
            fixedImage = React.cloneElement(image as React.ReactElement, { imageSettings });
        }

        if (heading) {
            return (
                <Node {...HeadingContainer}>
                    {' '}
                    <Node tag='i' className='ms-footer__heading__title-icon' aria-hidden='true' />
                    {heading}{' '}
                </Node>
            );
        } else if (Link && fixedImage) {
            return (
                <Node {...Link}>
                    {linkText}
                    {fixedImage}
                </Node>
            );
        } else if (Link) {
            return EditableLink;
        }
        return (
            <>
                {text}
                {fixedImage}
            </>
        );
    }
}

export default FooterItemView;
